import { Card, CardBody, Image, Button, Input, Textarea, Select, SelectItem } from '@heroui/react'
import { useState, useRef } from 'react'
import { PhotoIcon, ArrowUpTrayIcon, PencilIcon, CheckIcon, XMarkIcon, VideoCameraIcon, CheckCircleIcon, XCircleIcon, ClockIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { updateExerciseDetails, uploadExerciseThumbnail, uploadExerciseVideoPreview } from '../../services/api'

const ExerciseHeader = ({item, onChange = () => {}}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    title: item.title || '',
    description: item.description || '',
    repetition: item.repetition || 3,
    holdDuration: item.holdDuration || 5,
    qaStatus: item.qaStatus || 'new'
  });
  const [isSaving, setIsSaving] = useState(false);
  const [videoKey, setVideoKey] = useState(0); // Force video re-render
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // QA Status configuration
  const qaStatusOptions = [
    { key: 'new', label: 'New Exercise', color: 'default', bgColor: 'bg-gray-600', icon: SparklesIcon },
    { key: 'wip', label: 'Work in Progress', color: 'warning', bgColor: 'bg-orange-600', icon: ClockIcon },
    { key: 'passed', label: 'QA Passed', color: 'success', bgColor: 'bg-green-600', icon: CheckCircleIcon }
  ];

  const getQaStatusInfo = (status) => {
    return qaStatusOptions.find(option => option.key === status) || qaStatusOptions[0];
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewImage(e.target.result);
    };
    reader.readAsDataURL(file);

    // Upload to server
    setIsUploading(true);
    try {
      const response = await uploadExerciseThumbnail(item.id, file);
      //console.log('Thumbnail uploaded successfully:', response.data);
      setIsUploading(false);
      
      // Update the item with new thumbnail path
      if (response.data && response.data.thumbnailPath) {
        // Update the item's thumbnailPath
        item.thumbnailPath = response.data.thumbnailPath;
        // Trigger a re-render by calling onChange if available
        if (onChange) {
          onChange(item);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      setIsUploading(false);
      setPreviewImage(null);
      alert('Failed to upload image. Please try again.');
    }
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      alert('Please select a video file');
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      alert('Video size should be less than 50MB');
      return;
    }

    // Create preview
    const videoUrl = URL.createObjectURL(file);
    setPreviewVideo(videoUrl);

    // Upload to server
    setIsVideoUploading(true);
         try {
       const response = await uploadExerciseVideoPreview(item.id, file);
       console.log('Video uploaded successfully:', response.data);
       console.log('Response data keys:', Object.keys(response.data || {}));
       setIsVideoUploading(false);
       
               // Update the item with new video path
        if (response.data) {
          // Update the item's previewVideoPath - server returns previewVideoPath
                     if (response.data.previewVideoPath) {
             item.previewVideoPath = response.data.previewVideoPath;
             // Clear preview video to show the uploaded video
             setPreviewVideo(null);
             // Force video re-render
             setVideoKey(prev => prev + 1);
             // Trigger a re-render by calling onChange if available
             if (onChange) {
               onChange(item);
             }
             console.log('Video path updated:', response.data.previewVideoPath);
           } else {
             console.log('No previewVideoPath in response:', response.data);
           }
        }
     } catch (error) {
      console.error('Upload error:', error);
      setIsVideoUploading(false);
      setPreviewVideo(null);
      URL.revokeObjectURL(videoUrl);
      alert('Failed to upload video. Please try again.');
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleVideoButtonClick = () => {
    videoInputRef.current?.click();
  };

  const handleEditClick = () => {
    setEditData({
      title: item.title || '',
      description: item.description || '',
      repetition: item.repetition || 3,
      holdDuration: item.holdDuration || 5,
      qaStatus: item.qaStatus || 'new'
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!editData.title.trim()) {
      alert('Title cannot be empty');
      return;
    }

         if (editData.repetition < 1 || editData.repetition > 100) {
       alert('Repetition must be between 1 and 100');
       return;
     }

    setIsSaving(true);
    try {
      const response = await updateExerciseDetails(item.id, editData);
      console.log('Exercise updated successfully:', response.data);
      setIsEditing(false);
      setIsSaving(false);
      // Update the item with new data
      if (response.data) {
        // Update item properties
        item.title = editData.title;
        item.description = editData.description;
        item.repetition = editData.repetition;
        item.qaStatus = editData.qaStatus;
        // Note: holdDuration is not updated as it's calculated server-side
        
        // Trigger a re-render by calling onChange
        onChange(item);
      }
    } catch (error) {
      console.error('Update error:', error);
      setIsSaving(false);
      alert('Failed to update exercise. Please try again.');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({
      title: item.title || '',
      description: item.description || '',
      repetition: item.repetition || 3,
      holdDuration: item.holdDuration || 5,
      qaStatus: item.qaStatus || 'new'
    });
  };

     const currentImageSrc = previewImage || item.thumbnailPath;
   const currentVideoSrc = previewVideo || item.previewVideoPath;
   
   // Debug logging
   /* console.log('Current video source:', currentVideoSrc);
   console.log('Preview video:', previewVideo);
   console.log('Item previewVideoPath:', item.previewVideoPath);
   console.log('Video key:', videoKey); */

    return (
        <div className="flex flex-row gap-4">
      {/* Left Card - Thumbnail & Info */}
      <Card 
        isBlurred
        className="border border-gray-200 bg-white dark:bg-gray-800 shadow-lg max-w-[610px] transition-all duration-300"
        shadow="sm"
      >
          <CardBody className="flex gap-3">
          <div className="size-fit relative">
                <Image
                alt="Exercise cover"
              className="object-contain rounded-lg shadow-md"
                height={200}
              src={currentImageSrc}
                width="100%"
                />
            
            {/* Upload Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center group">
              <div 
                className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                onClick={handleButtonClick}
              >
                <div className="bg-white rounded-lg p-2 shadow-lg">
                  {isUploading ? (
                    <div className="flex items-center gap-2 text-gray-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span className="text-sm">Uploading...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-gray-600">
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      <span className="text-sm">Upload</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col justify-center flex-1">
            <div className="mb-2">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {item.id}
              </h1>
            </div>
            
            {/* Title Section */}
            <div className="mb-2">
              {isEditing ? (
                <Input
                  value={editData.title}
                  onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter exercise title"
                  className="mb-2"
                  size="sm"
                />
              ) : (
                <p className="text-md font-medium text-gray-700 dark:text-gray-300">
                  {item.title}
                </p>
              )}
            </div>
            
            {/* Description Section */}
            <div className="mb-3">
              {isEditing ? (
                <Textarea
                  value={editData.description}
                  onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter exercise description"
                  size="sm"
                  rows={2}
                />
              ) : (
                <p className="text-small text-gray-600 dark:text-gray-400">
                  {item.description}
                </p>
              )}
            </div>

            {/* Repetition and Hold Duration Section */}
            <div className="mb-3">
              {isEditing ? (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">Repetition</label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={editData.repetition}
                      onChange={(e) => setEditData(prev => ({ ...prev, repetition: parseInt(e.target.value) || 1 }))}
                      placeholder="3"
                      size="sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">Hold Duration (sec)</label>
                    <Input
                      type="number"
                      min="1"
                      max="300"
                      value={editData.holdDuration}
                      isReadOnly
                      placeholder="5"
                      size="sm"
                      description="Calculated automatically from video"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Repetition</label>
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                      {item.repetition || 3} times
                    </p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Hold Duration</label>
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                      {item.holdDuration || 5} seconds
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* QA Status Section */}
            <div className="mb-3">
              {isEditing ? (
                <div>
                  <label className="text-xs text-gray-600 dark:text-gray-400 mb-1 block">QA Status</label>
                  <select
                    value={editData.qaStatus || 'new'}
                    onChange={(e) => setEditData(prev => ({ ...prev, qaStatus: e.target.value }))}
                    className="w-full px-3 py-2 text-sm rounded-md bg-black text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {qaStatusOptions.map((option) => {
                      const IconComponent = option.icon;
                      return (
                        <option key={option.key} value={option.key}>
                          {option.label}
                        </option>
                      );
                    })}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">QA Status</label>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const statusInfo = getQaStatusInfo(item.qaStatus || 'new');
                      const IconComponent = statusInfo.icon;
                      return (
                        <div className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${statusInfo.bgColor}`}>
                          <IconComponent className="h-3 w-3 text-white" />
                          <span className="text-white">{statusInfo.label}</span>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button
                    size="sm"
                    color="success"
                    startContent={<CheckIcon className="h-4 w-4" />}
                    onPress={handleSave}
                    isLoading={isSaving}
                    className="flex-1"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    size="sm"
                    color="danger"
                    variant="bordered"
                    startContent={<XMarkIcon className="h-4 w-4" />}
                    onPress={handleCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="bordered"
                    startContent={<PencilIcon className="h-4 w-4" />}
                    onPress={handleEditClick}
                    className="flex-1"
                  >
                    Edit Details
                  </Button>
                  <Button
                    size="sm"
                    variant="bordered"
                    startContent={<PhotoIcon className="h-4 w-4" />}
                    disabled={isUploading}
                    onPress={handleButtonClick}
                  >
                    {isUploading ? 'Uploading...' : 'Thumbnail'}
                  </Button>
                </>
              )}
            </div>
            </div>
          </CardBody>
        </Card>

      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
        disabled={isUploading}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        onChange={handleVideoUpload}
        className="hidden"
        disabled={isVideoUploading}
      />

      {/* Right Card - Video */}
      <Card 
        isBlurred
        className="border border-gray-200 bg-white dark:bg-gray-800 shadow-lg max-w-[610px] flex-1 transition-all duration-300"
        shadow="sm"
      >
        <CardBody className="flex gap-3 p-0 relative">
          <div className="w-full h-full relative group">
                         <video 
               key={`${currentVideoSrc}-${videoKey}`} // Force re-render with videoKey
               className="w-full h-full object-cover rounded-lg" 
               controls
               preload="metadata"
               onLoadStart={() => console.log('Video loading started:', currentVideoSrc)}
               onLoadedData={() => console.log('Video loaded:', currentVideoSrc)}
             >
               <source src={`${currentVideoSrc}?t=${videoKey}`} type="video/mp4"/>
                Your browser does not support the video tag.
                </video>
            
            {/* Video Upload Overlay - Only on hover and not covering controls */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center pointer-events-none">
              <div 
                className="cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-auto"
                onClick={handleVideoButtonClick}
              >
                <div className="bg-white rounded-lg p-2 shadow-lg">
                  {isVideoUploading ? (
                    <div className="flex items-center gap-2 text-gray-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span className="text-sm">Uploading...</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-gray-600">
                      <VideoCameraIcon className="h-4 w-4" />
                      <span className="text-sm">Change Video</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            </div>
          </CardBody>
        </Card>
    </div>
    );
}

export default ExerciseHeader;